import { Box } from '@material-ui/core';
import { formatBillingFrequency } from 'utils/MembershipUtils';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { ClientsStatus, getClientProfileOverviewThunk } from '@spike/clients-action';
import { Client as ClientModel } from '@spike/client-model';
import Pet from '@spike/pet-model';
import { fetchPetsThunk, PetsStatus } from '@spike/pets-action';
import useNonInitialEffect from '@versiondos/hooks';
import Header from 'components/Checkout2/UI/Header';
import ClientSearch from 'components/CreateBooking/ClientSearch';
import { Spinner } from 'components/UI';
import { useApiClientWrapper, usePermissions, useRoles } from 'hooks';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import Footer from './Footer';
import InfoAlert from './InfoAlert';
import PetsMembership from './PetsMermbership';
import { SellInfo } from './SellMembership';
import Step from './Step';
import EditPriceModal from './Modals/EditPriceModal/EditPriceModal';
import { Membership, EnrollmentPending, MembershipEnrollmentType } from '@spike/membership-model';
import { addEnrollmentThunk, MembershipsStatus } from '@spike/memberships-action';

export interface MainStepProps {
    sellInfo: SellInfo;
    fromClientMemberships?: boolean;
    onChange: (sellInfo: SellInfo) => void;
    onClose: () => void;
    onEdit: () => void;
    onAddClient: () => void;
    onCheckout: (orderId: number, enrollmentId: number, enrollmentType: string) => void;
    className?: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        petsMembershipContainer: {
            marginTop: 4,
            paddingTop: 24,
            borderTop: '1px solid #D4D4D4',
            height: '100%'
        }
    })
);

export const MainStep: FunctionComponent<MainStepProps> = props => {
    const classes = useStyles(props);

    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const permissions = usePermissions();
    const { isOwner } = useRoles();

    const clientsStatus = useSelector<RootState, ClientsStatus>(state => state.clients.status);
    const currentClient = useSelector<RootState, ClientModel | undefined>(state => state.clients.client);

    const pets = useSelector<RootState, Array<Pet>>(state => state.pets.list);
    const petsStatus = useSelector<RootState, PetsStatus>(state => state.pets.status);

    const memberships = useSelector<RootState, Array<Membership>>(state => state.memberships.list);
    const membershipsStatus = useSelector<RootState, MembershipsStatus>(state => state.memberships.status);
    const enrollment = useSelector<RootState, EnrollmentPending | null>(state => state.memberships.enrollment);

    const [loading, setLoading] = useState({ client: false, pets: false, checkout: false });
    const [sellInfo, setSellInfo] = useState(props.sellInfo);
    const [showEditPrice, setShowEditPrice] = useState(false);

    useEffect(() => {
        if (!sellInfo.clientId) return;
        dispatch(getClientProfileOverviewThunk(apiClientWrapper, sellInfo.clientId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sellInfo.clientId]);

    useEffect(() => {
        if (props.sellInfo.membershipId === undefined) {
            return;
        }

        setSellInfo(prev =>
            prev.membershipId === undefined ? { ...prev, membershipId: props.sellInfo.membershipId } : prev
        );
    }, [props.sellInfo.membershipId]);

    useNonInitialEffect(() => {
        props.onChange(sellInfo);
    }, [sellInfo]);

    useNonInitialEffect(() => {
        switch (clientsStatus) {
            case ClientsStatus.GetSuccess:
            case ClientsStatus.Error:
                setLoading(prev => ({ ...prev, client: false }));
                break;
        }
    }, [clientsStatus]);

    useNonInitialEffect(() => {
        switch (petsStatus) {
            case PetsStatus.FetchSuccess:
            case PetsStatus.Error:
                setLoading(prev => ({ ...prev, pets: false }));
                break;
        }
    }, [petsStatus]);

    useNonInitialEffect(() => {
        switch (membershipsStatus) {
            case MembershipsStatus.AddEnrollmentSuccess:
                setLoading(prev => ({ ...prev, checkout: false }));
                enrollment &&
                    selectedMembership &&
                    props.onCheckout &&
                    props.onCheckout(enrollment.orderId, enrollment.id, selectedMembership.enrollmentType);
                break;
            case MembershipsStatus.AddEnrollmentError:
                setLoading(prev => ({ ...prev, checkout: false }));
                break;
        }
    }, [membershipsStatus]);

    const selectedPets = useMemo(
        () => pets.filter(pet => sellInfo.petIds.includes(pet.id ?? 0)),
        [pets, sellInfo.petIds]
    );

    const selectedMembership = useMemo(
        () => memberships.find(membership => membership.id === sellInfo.membershipId),
        [memberships, sellInfo.membershipId]
    );

    // Price override is available for both packages and subscriptions. For subscriptions the
    // override applies to the first billing cycle only (renewals use the configured price).
    const isSubscription = selectedMembership?.enrollmentType === MembershipEnrollmentType.SUBSCRIPTION;
    const canEditPrice = !!selectedMembership && (permissions.checkout.editPrices || isOwner);
    const catalogPrice = Number(selectedMembership?.total ?? 0);
    const displayedTotal = sellInfo.priceOverride ?? catalogPrice;
    const frequencyLabel =
        isSubscription && selectedMembership?.billingFrequency ? formatBillingFrequency(selectedMembership) : undefined;
    const selectedClientName =
        currentClient && currentClient.id === sellInfo.clientId
            ? `${currentClient.firstName} ${currentClient.lastName}`
            : undefined;
    const isLockedClientFromMemberships =
        !!props.fromClientMemberships && !!sellInfo.clientId && currentClient?.id === sellInfo.clientId;

    const clearSellInfo = () => setSellInfo({ petIds: [] });

    const setClientId = (clientId?: number) => setSellInfo(prev => ({ ...prev, clientId }));

    const removePetId = (petId: number) =>
        setSellInfo(prev => ({ ...prev, petIds: prev.petIds.filter(id => id != petId) }));

    const selectClientHandler = (clientId: number) => {
        if (clientId === 0) {
            clearSellInfo();
        } else {
            setClientId(clientId);
            setLoading(prev => ({ ...prev, client: true, pets: true }));
            dispatch(fetchPetsThunk(apiClientWrapper, clientId));
        }
    };

    const checkoutHandler = () => {
        if (sellInfo.clientId && sellInfo.membershipId) {
            setLoading(prev => ({ ...prev, checkout: true }));
            dispatch(
                addEnrollmentThunk(
                    apiClientWrapper,
                    sellInfo.membershipId,
                    sellInfo.clientId,
                    sellInfo.petIds,
                    sellInfo.startAt || null,
                    sellInfo.endAt || null,
                    sellInfo.consumedPreset,
                    sellInfo.priceOverride
                )
            );
        }
    };

    const header = (
        <Header
            title="Sell Membership"
            onClose={props.onClose}
        />
    );

    const body = (
        <>
            <ClientSearch
                fromClient={!!sellInfo.clientId && currentClient?.id === sellInfo.clientId}
                setPetParent={(clientId: number, clientName: string) => selectClientHandler(clientId)}
                onAddClient={props.onAddClient}
                parentID={sellInfo.clientId}
                parentName={selectedClientName}
                readOnly={isLockedClientFromMemberships}
            />
            {sellInfo.clientId && !loading.client && (
                <Box className={classes.petsMembershipContainer}>
                    {loading.pets && <Spinner />}
                    {!loading.pets && (
                        <PetsMembership
                            pets={selectedPets}
                            membership={selectedMembership}
                            onEdit={props.onEdit}
                            onDeletePet={removePetId}
                        />
                    )}
                </Box>
            )}
        </>
    );

    const footer = (
        <Footer
            total={displayedTotal}
            buttonLabel="Checkout"
            disableButton={!sellInfo.clientId || !sellInfo.membershipId || sellInfo.petIds.length === 0}
            loading={loading.checkout}
            canEditPrice={canEditPrice}
            onEditPrice={() => setShowEditPrice(true)}
            frequencyLabel={frequencyLabel}
            note={
                isSubscription && sellInfo.priceOverride != null ? (
                    <InfoAlert>
                        This price applies to the first billing cycle only. Subsequent charges will use the regular
                        rate.
                    </InfoAlert>
                ) : undefined
            }
            onClickButton={checkoutHandler}
        />
    );

    return (
        <>
            <Step
                header={header}
                body={body}
                footer={footer}
            />
            {showEditPrice && (
                <EditPriceModal
                    currentPrice={displayedTotal}
                    onCancel={() => setShowEditPrice(false)}
                    onSave={newPrice => {
                        setSellInfo(prev => ({ ...prev, priceOverride: newPrice }));
                        setShowEditPrice(false);
                    }}
                />
            )}
        </>
    );
};

export default MainStep;
